import { Chip, ChipProps } from '@mui/material';
import { getGridStringOperators, GridColDef } from '@mui/x-data-grid-pro';
import sortBy from 'lodash/sortBy';

import { FILTER_NAMES } from 'common/src/query-params/constants';
import { SingleOperators } from 'common/src/query-params/operators';
import { BillEntryMethod } from 'common/src/types/enums/BillEntryMethod.enum';
import { Service } from 'common/src/types/enums/Service.enum';
import {
  IAccount,
  IBill,
  IFacility,
  IMeter,
  IUtility,
} from 'common/src/types/interfaces';

import { NON_THEME_COLORS } from 'portal/src/theme/constants';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import { needsAllocationReview } from './helpers';

import { FreeSelectorComponent } from '../../common/Filters/CustomCollectorComponents';
import {
  combineFilterOperators,
  customDateFilter,
  customNumberFilter,
  multipleFilterOperators,
  serverSideStringFilterOperators,
  singleSelectFilter,
} from '../../common/Filters/CustomCollectors';
import {
  customArrayOperators,
  customSingleOperators,
} from '../../common/Filters/CustomFilterOperators';
import addBooleanColumnProps from '../../common/tables/addBooleanColumnProps';
import addDateColumnProps from '../../common/tables/addDateColumnProps';

const combinedSingleExcludeOperators = [
  SingleOperators.GREATER_THAN,
  SingleOperators.GREATER_THAN_OR_EQUAL_TO,
  SingleOperators.LESS_THAN,
  SingleOperators.LESS_THAN_OR_EQUAL_TO,
];

const billEntryMethodsService = {
  data: Object.values(BillEntryMethod).map((value) => ({ value })),
  fieldset: { label: 'value', value: 'value', key: 'value' },
};

const getBillEntryMethodChipProps = (
  value: BillEntryMethod | string,
): Pick<ChipProps, 'variant' | 'sx'> => {
  const { purple, teal, pink, gray, orange } = NON_THEME_COLORS;

  switch (value) {
    case BillEntryMethod.Allocated:
      return {
        variant: 'filled',
        sx: { color: 'white', backgroundColor: purple },
      };
    case BillEntryMethod.Automatic:
      return {
        variant: 'filled',
        sx: { color: 'white', backgroundColor: teal },
      };
    case BillEntryMethod.Imported:
      return {
        variant: 'filled',
        sx: { color: 'white', backgroundColor: pink },
      };
    case BillEntryMethod.Manual:
      return {
        variant: 'outlined',
        sx: { color: gray, borderColor: gray },
      };
    case BillEntryMethod.Transferred:
      return {
        variant: 'filled',
        sx: { color: 'white', backgroundColor: orange },
      };
    default:
      return {
        variant: 'filled',
        sx: { color: 'white', backgroundColor: gray },
      };
  }
};

const buildBillsColumns = (
  disableNeedsReviewSort: boolean,
  accounts: IAccount[],
  facilities: IFacility[],
  meters: IMeter[],
  utilities: IUtility[],
): GridColDef[] => {
  const getAccountGroupColumn = (): GridColDef => {
    const accountGroupFieldName = 'accountGroup';
    const fieldset = {
      label: accountGroupFieldName,
      value: accountGroupFieldName,
      key: accountGroupFieldName,
    };

    const accountGroupData = sortBy(
      Array.from(
        new Set(
          accounts
            .map(({ accountGroup }) => accountGroup)
            .filter((accountGroup) => !!accountGroup),
        ),
      ).map((accountGroup) => ({ accountGroup })),
      accountGroupFieldName,
    );
    const service = { data: accountGroupData, fieldset };

    return {
      field: accountGroupFieldName,
      headerName: 'Account Group',
      flex: 1,
      minWidth: 160,
      sortingOrder: ['asc', 'desc'],
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter(
          { service },
          combinedSingleExcludeOperators,
        ),
        multipleOperators: multipleFilterOperators({ service }),
      }),
      valueGetter: (_, row: IBill) => row.account?.accountGroup ?? '',
    };
  };

  const getServiceTypeColumn = (): GridColDef => {
    const serviceTypeFieldName = 'serviceType';
    const fieldset = {
      label: serviceTypeFieldName,
      value: serviceTypeFieldName,
    };
    const service = {
      data: Object.values(Service).map((value) => ({ serviceType: value })),
      fieldset,
    };

    return {
      field: serviceTypeFieldName,
      headerName: 'Service Type',
      flex: 1,
      minWidth: 160,
      sortingOrder: ['asc', 'desc'],
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter(
          { service },
          combinedSingleExcludeOperators,
        ),
        multipleOperators: multipleFilterOperators({ service }),
      }),
      valueGetter: (_, row: IBill) =>
        [...new Set(row.lineItems.map(({ meter }) => meter.serviceType))].join(
          ', ',
        ),
    };
  };

  const stringOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'isAnyOf'].includes(value),
  );

  const HIDDEN_FILTER_OPERATORS = [
    {
      ...stringOperators.find(({ value }) => value === 'isAnyOf'),
      ...customArrayOperators[0],
      InputComponent: FreeSelectorComponent,
    },
    {
      ...stringOperators.find(({ value }) => value === 'equals'),
      ...customSingleOperators[0],
    },
  ];

  return [
    {
      field: 'id',
      headerName: 'ID',
      filterOperators: HIDDEN_FILTER_OPERATORS,
      hideable: false,
    },
    {
      field: 'statementId',
      headerName: 'Statement ID',
      filterOperators: HIDDEN_FILTER_OPERATORS,
      hideable: false,
    },
    {
      field: 'startDate',
      filterOperators: customDateFilter(),
      sortingOrder: ['asc', 'desc'],
      ...addDateColumnProps('Start Date'),
    },
    {
      field: 'endDate',
      filterOperators: customDateFilter(),
      sortingOrder: ['asc', 'desc'],
      ...addDateColumnProps('End Date'),
    },
    {
      field: 'billEntryMethod',
      headerName: 'Entry Method',
      width: 109,
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter({
          service: billEntryMethodsService,
        }),
        multipleOperators: multipleFilterOperators({
          service: billEntryMethodsService,
        }),
      }),
      renderCell: ({ value }) => {
        const { variant, sx } = getBillEntryMethodChipProps(value);

        return <Chip label={value} sx={sx} variant={variant} />;
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      filterOperators: customNumberFilter(),
      sortingOrder: ['asc', 'desc'],
      sortComparator: (v1, v2) =>
        Number(v1 === 'Allocated' ? 0 : v1) -
        Number(v2 === 'Allocated' ? 0 : v2),
      renderCell: (params) =>
        params.row.isAllocated ? 'Allocated' : numberFormatter(params.value),
    },
    {
      field: 'date',
      filterOperators: customDateFilter(),
      sortingOrder: ['asc', 'desc'],
      ...addDateColumnProps('Date'),
    },
    {
      field: 'budgetPeriod',
      filterOperators: customDateFilter(),
      sortingOrder: ['asc', 'desc'],
      ...addDateColumnProps('Budget Period', ...[,], 'MMMM yyyy'),
    },
    {
      field: 'utilityId',
      headerName: 'Utility',
      minWidth: 160,
      flex: 1,
      sortingOrder: ['asc', 'desc'],
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter(
          {
            service: {
              data: utilities,
              fieldset: { label: 'name', value: 'id', key: 'id' },
            },
          },
          combinedSingleExcludeOperators,
        ),
        multipleOperators: multipleFilterOperators({
          service: {
            data: utilities,
            fieldset: { label: 'name', value: 'id', key: 'id' },
          },
        }),
      }),
      valueGetter: (_, row: IBill) => row.account?.utility.name ?? '',
    },
    {
      field: 'accountId',
      headerName: 'Account Number',
      minWidth: 160,
      flex: 1,
      sortingOrder: ['asc', 'desc'],
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter(
          {
            service: {
              data: sortBy(accounts, (a) => a.number.toLowerCase()),
              fieldset: { label: 'number', value: 'id', key: 'id' },
            },
          },
          combinedSingleExcludeOperators,
        ),
        multipleOperators: multipleFilterOperators({
          service: {
            data: sortBy(accounts, (a) => a.number.toLowerCase()),
            fieldset: { label: 'number', value: 'id', key: 'id' },
          },
        }),
      }),
      valueGetter: (_, row: IBill) => row.account?.number ?? '',
    },
    getAccountGroupColumn(),
    {
      field: 'department',
      headerName: 'Department',
      flex: 1,
      minWidth: 160,
      sortingOrder: ['asc', 'desc'],
      filterOperators: serverSideStringFilterOperators,
      valueGetter: (_, row: IBill) => row.account?.department ?? '',
    },
    {
      field: 'facilityId',
      headerName: 'Facilities',
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter(
          {
            service: {
              data: facilities,
              fieldset: { label: 'name', value: 'id', key: 'id' },
            },
          },
          combinedSingleExcludeOperators,
        ),
        multipleOperators: multipleFilterOperators({
          service: {
            data: facilities,
            fieldset: { label: 'name', value: 'id', key: 'id' },
          },
        }),
      }),
      sortable: false,
      width: 160,
      valueGetter: (_, row: IBill) => {
        const facilityNames = [
          ...new Set(row.lineItems?.map((li) => li.meter.parentFacility.name)),
        ];

        return facilityNames?.join(', ') ?? '';
      },
    },
    {
      field: 'meterId',
      headerName: 'Meters',
      filterOperators: combineFilterOperators({
        singleOperators: singleSelectFilter(
          {
            service: {
              data: sortBy(meters, (m) => m.name.toLowerCase()),
              fieldset: { label: 'name', value: 'id', key: 'id' },
            },
          },
          combinedSingleExcludeOperators,
        ),
        multipleOperators: multipleFilterOperators({
          service: {
            data: sortBy(meters, (m) => m.name.toLowerCase()),
            fieldset: { label: 'name', value: 'id', key: 'id' },
          },
        }),
      }),
      sortable: false,
      width: 160,
      valueGetter: (_, row: IBill) => {
        const meters = [...new Set(row.lineItems?.map((li) => li.meter.name))];
        return meters?.join(', ') ?? '';
      },
    },
    getServiceTypeColumn(),
    {
      field: 'isPaid',
      headerName: 'Paid',
      width: 112,
      ...addBooleanColumnProps({ filterMode: 'server' }),
    },
    {
      field: 'needsReview',
      headerName: 'Needs Review',
      width: 112,
      sortable: !disableNeedsReviewSort,
      valueGetter: (value, row: IBill) => {
        return !!value || needsAllocationReview(row);
      },
      ...addBooleanColumnProps({
        filterMode: 'server',
        displayLogic: { valueForYes: true, yesIsGreen: false },
        serverAdditionalFilters: (value) => [
          {
            field: FILTER_NAMES.computedNeedsReview,
            operator: SingleOperators.EQUALS,
            value,
          },
        ],
        replaceBaseFilter: () => true,
      }),
    },
    {
      field: 'image',
      headerName: 'Image',
      filterOperators: HIDDEN_FILTER_OPERATORS,
      hideable: false,
    },
  ];
};

export default buildBillsColumns;
